import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { HeartPulse, TrendingUp, TrendingDown, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compact Care Watchlist tile — matches the visual grammar of the sibling
// Dashboard KPI tiles (same padding, border, icon-in-square treatment).
// Deliberately restrained: Everperform already surfaces the detail inside
// their own product, this tile just nudges leaders that something warrants
// a check-in without stealing the whole Dashboard's attention.
const CareWatchlistTile = ({ onOpen }) => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/care-watchlist`);
      setData(res.data);
    } catch {
      setData({ counts: { critical: 0, watch: 0 }, peakAndFalling: [] });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const critical = data?.counts?.critical ?? 0;
  const watch = data?.counts?.watch ?? 0;
  const peakFalling = data?.peakAndFalling?.length ?? 0;
  const total = critical + watch;
  const allClear = total === 0;

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="care-watchlist-tile"
      className="relative p-5 rounded-[10px] border border-[#E5E5E5] bg-white card-hover overflow-hidden text-left transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-rose-200"
      style={{ boxShadow: 'var(--card-shadow)' }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg ${allClear ? 'bg-emerald-100' : 'bg-rose-100'}`}>
          <HeartPulse className={`w-4 h-4 ${allClear ? 'text-emerald-500' : 'text-rose-500'}`} />
        </div>
        <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${allClear ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-600'}`}>
          {allClear ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          <span>{allClear ? 'All clear' : `${peakFalling} peak+falling`}</span>
        </div>
      </div>
      <div>
        <h3 className="text-xl font-bold tracking-tight mb-0.5 text-[#171717]" data-testid="care-tile-total">
          {total}
        </h3>
        <p className="text-sm font-medium text-[#525252]">Care Watchlist</p>
        <div className="text-xs mt-1.5 flex items-center gap-3 text-[#A3A3A3]">
          <span>
            <span className="font-semibold text-rose-600" data-testid="care-tile-critical">{critical}</span> critical
          </span>
          <span>
            <span className="font-semibold text-amber-600" data-testid="care-tile-watch">{watch}</span> watch
          </span>
          <span className="ml-auto inline-flex items-center gap-0.5 text-[#525252] font-medium">
            View <ChevronRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </button>
  );
};

export default CareWatchlistTile;
